import React, { useState } from "react";
import { motion } from "framer-motion";
import { FileText, Download, Search, Filter } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { DOWNLOADS_PAGE } from "../config/content";
import Header from "./Header";
import Footer from "./Footer";

export default function DownloadsPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const filteredFiles = DOWNLOADS_PAGE.files.filter(file => {
    const matchesSearch = file.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         file.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === "All" || file.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="overline text-[#0047FF] mb-4">Resources</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 mb-6">
              {DOWNLOADS_PAGE.hero.title}
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {DOWNLOADS_PAGE.hero.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-8 bg-white border-b border-slate-200 sticky top-16 md:top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-none"
              />
            </div>
            
            {/* Category Filter */}
            <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto">
              {DOWNLOADS_PAGE.categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-all ${
                    activeCategory === category
                      ? "bg-[#0047FF] text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Downloads Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {filteredFiles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredFiles.map((file, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-slate-50 border border-slate-200 p-6 hover:border-[#0047FF] transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#0047FF] text-white flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <span className="text-xs font-semibold text-[#0047FF] uppercase">{file.category}</span>
                      <h3 className="font-heading text-lg font-semibold text-slate-900 mt-1">
                        {file.name}
                      </h3>
                      <p className="text-slate-600 text-sm mt-2">
                        {file.description}
                      </p>
                      <div className="flex items-center gap-4 mt-4">
                        <span className="text-xs text-slate-500">{file.fileType}</span>
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm font-medium text-[#0047FF] hover:underline"
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </a>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No documents found</h3>
              <p className="text-slate-600">Try adjusting your search or filter criteria.</p>
            </div>
          )}
        </div>
      </section>

      {/* Info Box */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-white border border-slate-200 p-8 text-center">
            <h3 className="font-heading text-2xl font-bold text-slate-900 mb-4">
              Need More Documents?
            </h3>
            <p className="text-slate-600 mb-6">
              If you're looking for specific product documentation, technical specifications, or certificates, 
              please contact our team and we'll be happy to provide them.
            </p>
            <Button 
              onClick={() => window.location.href = '/contact'}
              className="bg-[#0047FF] hover:bg-[#0037CC] text-white rounded-none px-8"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
